import { AnimatePresence, motion } from "framer-motion";
import {
  Activity, BookOpen, Bot, Boxes, Brain, ChevronLeft, ClipboardList, FileText, Flame,
  Gauge, LayoutDashboard, LogOut, Menu, PlugZap, Settings, Sparkles, UploadCloud, Users, X, RefreshCw, CloudOff
} from "lucide-react";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Navigate, NavLink, useLocation, useNavigate } from "./router";
import type { ReactNode } from "react";
import { roleLabels, type Role, xpThreshold } from "@lovmia/shared";
import { api, ApiError } from "./api";
import { AppStateProvider, clearBootstrapCache, useAppState, useBootstrap } from "./state";
import { currentWeekLabel, currentWeekRangeLabel } from "./week";
import { LoginPage } from "./pages/LoginPage";
import { ReviewPage } from "./pages/ReviewPage";
import { BoardPage } from "./pages/BoardPage";
import { NotesPage } from "./pages/NotesPage";
import { DiagnosticsPage } from "./pages/DiagnosticsPage";
import { LearningsPage } from "./pages/LearningsPage";
import { RegistryPage } from "./pages/RegistryPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TeamPage } from "./pages/TeamPage";
import { PipelinePage } from "./pages/PipelinePage";
import { DayZeroPage } from "./pages/DayZeroPage";
import { StatsPage } from "./pages/StatsPage";

type NavItem = { to: string; label: string; icon: typeof Activity; roles: Role[]; badge?: "review" | "notes" | "learnings" };

const nav: NavItem[] = [
  { to: "/board", label: "Доска", icon: Boxes, roles: ["admin", "user"] },
  { to: "/review", label: "Разбор", icon: LayoutDashboard, roles: ["admin", "user"], badge: "review" },
  { to: "/diagnostics", label: "Диагностика", icon: Activity, roles: ["admin", "user"] },
  { to: "/learnings", label: "Learnings", icon: Brain, roles: ["admin"], badge: "learnings" },
  { to: "/registry", label: "Таблица", icon: ClipboardList, roles: ["admin", "user"] },
  { to: "/stats", label: "Статистика", icon: Gauge, roles: ["admin", "user"] },
  { to: "/settings", label: "Настройки", icon: Settings, roles: ["admin"] }
];

/**
 * Состояние живого канала. Важна не красота, а честность: пользователь должен
 * понимать, видит он доску в реальном времени или с задержкой опроса.
 */
function LiveBadge() {
  const { liveState } = useAppState();
  const label = liveState === "live" ? "живое обновление" : liveState === "polling" ? "обновление раз в 20 сек" : "подключаемся";
  return <span className={`live-badge live-badge--${liveState}`} title={label}>
    <i />{liveState === "live" ? "live" : liveState === "polling" ? "poll" : "…"}
  </span>;
}

function LoadingScreen() {
  return <div className="boot-screen"><div className="brand-mark brand-mark--large">L</div><div className="boot-line"><i /></div><span>Собираем рабочий контур</span></div>;
}

/** «14:32» сегодня, «26 окт 14:32» — раньше: снимок мог пролежать с прошлого дня. */
function snapshotTime(savedAt: number) {
  const date = new Date(savedAt);
  const time = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" }).format(date);
  if (date.toDateString() === new Date().toDateString()) return time;
  return `${new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short" }).format(date)} ${time}`;
}

/**
 * Доска нарисована из снимка в браузере, свежий bootstrap ещё не доехал или
 * не доехал вовсе. Плашка тихая: работать по снимку можно, но видно, от
 * какого он времени.
 */
function SnapshotBadge({ savedAt, failed, retry }: { savedAt: number; failed: boolean; retry: () => void }) {
  if (!failed) return <span className="snapshot-badge" title="Доска из кеша браузера, свежие данные загружаются">
    <RefreshCw size={12} className="spin" />данные от {snapshotTime(savedAt)}, обновляем
  </span>;
  return <button className="snapshot-badge snapshot-badge--failed" onClick={retry} title="Сервер не ответил. Нажмите, чтобы повторить">
    <CloudOff size={12} />данные от {snapshotTime(savedAt)}, сервер не ответил
  </button>;
}

function ProtectedLayout({ children }: { children: ReactNode }) {
  const query = useBootstrap();
  const queryClient = useQueryClient();
  const [mobileNav, setMobileNav] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const unauthorized = query.error instanceof ApiError && query.error.status === 401;
  // Сессия кончилась: снимок прошлого пользователя не должен всплыть после
  // входа под другим. Кеш браузера чистит сам запрос, здесь — память вкладки.
  useEffect(() => { if (unauthorized) queryClient.removeQueries(); }, [unauthorized, queryClient]);
  if (query.isLoading) return <LoadingScreen />;
  if (unauthorized) return <Navigate to="/login" replace />;
  if (!query.data) return <div className="error-screen"><strong>Не удалось открыть Creative Loop</strong><button onClick={() => query.refetch()}>Повторить</button></div>;
  const data = query.data;
  const available = nav.filter((item) => item.roles.includes(data.user.role));
  const badgeValue = (badge: NavItem["badge"]) => badge === "review" ? data.reviewItems.length : badge === "notes" ? data.notes.filter((note) => note.status === "parked").length : badge === "learnings" ? data.learnings.filter((item) => item.status === "candidate").length : 0;
  const logout = async () => {
    await api("/api/auth/logout", { method: "POST" });
    clearBootstrapCache();
    navigate("/login");
    queryClient.removeQueries();
  };

  return <AppStateProvider data={data}>
    <div className="app-shell">
      <aside className={`sidebar ${mobileNav ? "sidebar--open" : ""}`}>
        <div className="sidebar__brand">
          <div className="brand-mark">L</div>
          <div><strong>Lovmia</strong><span>Creative Loop</span></div>
          <button className="icon-button sidebar__close" onClick={() => setMobileNav(false)}><X size={18} /></button>
        </div>
        <nav className="sidebar__nav">
          <span className="nav-caption">Рабочий контур</span>
          {available.map((item) => {
            const Icon = item.icon;
            const count = badgeValue(item.badge);
            return <NavLink key={item.to} to={item.to} onClick={() => setMobileNav(false)} className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}>
              <Icon size={17} strokeWidth={1.8} /><span>{item.label}</span>{count > 0 && <b>{count}</b>}
            </NavLink>;
          })}
        </nav>
        <div className="sidebar__profile">
          <div className="avatar">{data.user.avatar}</div>
          <div><strong>{data.user.name}</strong><span>{roleLabels[data.user.role]} · LVL {data.user.level}</span></div>
          <button className="icon-button" onClick={logout} title="Выйти"><LogOut size={16} /></button>
        </div>
      </aside>
      {mobileNav && <button className="sidebar-scrim" onClick={() => setMobileNav(false)} aria-label="Закрыть меню" />}
      <main className="workspace">
        <header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setMobileNav(true)}><Menu size={20} /></button>
          <div className="crumb"><span>Creative Loop</span><i>/</i><strong>{location.pathname.startsWith("/hypotheses/") || location.pathname.startsWith("/creatives/") ? "Доска" : location.pathname.startsWith("/team") ? "Команда" : location.pathname.startsWith("/notes") ? "Заметки" : location.pathname.startsWith("/pipeline") ? "Инструкция" : available.find((item) => location.pathname.startsWith(item.to))?.label ?? "Workspace"}</strong></div>
          <div className="topbar__meta">
            {query.cachedAt != null && <SnapshotBadge savedAt={query.cachedAt} failed={query.isError && !query.isFetching} retry={() => void query.refetch()} />}
            <LiveBadge />
            <span className="week-badge">{currentWeekLabel()} <i>·</i> {currentWeekRangeLabel()}</span>
            <span className="streak"><Flame size={15} fill="currentColor" /> {data.user.streak} недель</span>
            <div className="xp" title={`До LVL ${data.user.level + 1} осталось ${Math.max(0, xpThreshold(data.user.level) - data.user.xp)} XP`}><span>LVL {data.user.level}</span><i><b style={{ width: `${Math.min(100, data.user.xp / xpThreshold(data.user.level) * 100)}%` }} /></i><em>{data.user.xp}/{xpThreshold(data.user.level)} · ещё {Math.max(0, xpThreshold(data.user.level) - data.user.xp)} XP</em></div>
          </div>
        </header>
        <AnimatePresence mode="wait">
          {/* Ключ — группа маршрута: /hypotheses/* это модалка ПОВЕРХ доски,
              страница под ней не должна перемонтироваться и фейдиться. */}
          <motion.div key={location.pathname.startsWith("/hypotheses/") || location.pathname.startsWith("/creatives/") ? "/board" : location.pathname} className="page-wrap" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: .12, ease: "easeOut" }}>
            {children}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  </AppStateProvider>;
}

function RouteContent() {
  const { pathname } = useLocation();
  if (pathname === "/" || pathname === "") return <Navigate to="/board" replace />;
  if (pathname === "/review") return <ReviewPage />;
  if (pathname === "/board" || pathname.startsWith("/hypotheses/") || pathname.startsWith("/creatives/")) return <BoardPage />;
  if (pathname === "/notes") return <NotesPage />;
  if (pathname === "/diagnostics") return <DiagnosticsPage />;
  if (pathname === "/learnings") return <LearningsPage />;
  if (pathname === "/registry") return <RegistryPage />;
  if (pathname === "/stats") return <StatsPage />;
  if (pathname === "/settings") return <SettingsPage />;
  if (pathname === "/team") return <TeamPage />;
  if (pathname === "/pipeline") return <PipelinePage />;
  if (pathname === "/day-zero") return <DayZeroPage />;
  return <Navigate to="/board" replace />;
}

export function App() {
  const { pathname } = useLocation();
  if (pathname === "/login") return <LoginPage />;
  return <ProtectedLayout><RouteContent /></ProtectedLayout>;
}
